import { useEffect, useState, useCallback, useContext } from "react";
import { Typography, Button, Grid } from "@mui/material";
import {
  getEventsByVenueId,
  getEvents,
  createEvent,
  editEvent,
  deleteEvent,
  sendPostEventEmail,
} from "../api";
import EventCard from "../components/EventCard";
import EventModal from "../components/EventModal";
import DeleteModal from "../components/DeleteModal";
import PostEventEmailModal from "../components/PostEventEmailModal";
import Loader from "../components/common/Loader";
import UserContext from "../contexts/UserContext";
import EmptyState from "../components/common/EmptyState";
import { NO_EVENTS, UNAPPROVED_DJ_STATUS } from "../constants";

const limit = 12;

const PHASES = ["tonight", "upcoming", "past"];

const SECTIONS = [
  { key: "tonight", label: "Tonight" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
];

const pad2 = (n) => String(n).padStart(2, "0");

const formatDate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Day boundary matches the mobile app + server cron jobs: before 9am, "today"
// is still the previous calendar date. tomorrow/yesterday are derived from
// that same rolled-back date, so Tonight and Upcoming can never overlap.
const getDayWindows = () => {
  const now = new Date();
  const today = new Date(now);
  if (now.getHours() < 9) today.setDate(today.getDate() - 1);
  today.setHours(12, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  return {
    today: formatDate(today),
    tomorrow: formatDate(tomorrow),
    yesterday: formatDate(yesterday),
  };
};

export default function EventsPage() {
  const userContext = useContext(UserContext);

  const role = userContext.user?.role;
  const grouped = ["dj", "admin"].includes(role);
  const isPendingDj = role === "dj" && userContext.user?.status === "pending";

  const [state, setState] = useState({
    buckets: { tonight: [], upcoming: [], past: [] },
    cursors: {},
    phase: "tonight",
    data: [],
    hasMore: true,
  });
  const [imageTimestamps, setImageTimestamps] = useState({});

  const totalItems = grouped
    ? state.buckets.tonight.length +
      state.buckets.upcoming.length +
      state.buckets.past.length
    : state.data.length;

  const drained = grouped ? state.phase === "done" : !state.hasMore;

  const emptyKey = isPendingDj
    ? UNAPPROVED_DJ_STATUS
    : drained && totalItems === 0 && !state.isLoading
      ? NO_EVENTS
      : "";

  const resetList = () => {
    window.scrollTo(0, 0);
    setState((s) => ({
      ...s,
      buckets: { tonight: [], upcoming: [], past: [] },
      cursors: {},
      phase: "tonight",
      data: [],
      hasMore: true,
    }));
  };

  const handleCreate = () => {
    setState((s) => ({
      ...s,
      open: true,
      event: undefined,
      handleConfirm: async (body) => {
        await createEvent(body);
        resetList();
      },
    }));
  };

  const handleEdit = (item) => () => {
    setState((s) => ({
      ...s,
      open: true,
      event: item,
      handleConfirm: async (body) => {
        const event = await editEvent(body);
        setImageTimestamps((m) => ({ ...m, [event.id]: Date.now() }));
        resetList();
      },
    }));
  };

  const handleClose = () => {
    setState((s) => ({
      ...s,
      open: false,
      event: undefined,
      handleConfirm: undefined,
    }));
  };

  const handleDelete = (item) => async () => {
    setState((s) => ({
      ...s,
      delete: true,
      deleteName: item.name,
      handleDeleteConfirm: async () => {
        await deleteEvent(item);
        setState((s) => ({
          ...s,
          data: s.data.filter((x) => x.id !== item.id),
          buckets: {
            tonight: s.buckets.tonight.filter((x) => x.id !== item.id),
            upcoming: s.buckets.upcoming.filter((x) => x.id !== item.id),
            past: s.buckets.past.filter((x) => x.id !== item.id),
          },
        }));
      },
    }));
  };

  const handleDeleteClose = () => {
    setState((s) => ({
      ...s,
      delete: false,
      deleteName: undefined,
      handleDeleteConfirm: undefined,
    }));
  };

  const handleSendPostEventEmail = (item) => async () => {
    setState((s) => ({
      ...s,
      sendPostEventEmail: true,
      event: item,
      handleSendPostEventEmailConfirm: async (body) => {
        await sendPostEventEmail(body);
        const flip = (x) =>
          x.id === item.id ? { ...x, postEventEmail: true } : x;
        setState((s) => ({
          ...s,
          data: s.data.map(flip),
          buckets: {
            tonight: s.buckets.tonight.map(flip),
            upcoming: s.buckets.upcoming.map(flip),
            past: s.buckets.past.map(flip),
          },
        }));
      },
    }));
  };

  const handleSendPostEventEmailClose = () => {
    setState((s) => ({
      ...s,
      sendPostEventEmail: false,
      event: undefined,
      handleSendPostEventEmailConfirm: undefined,
    }));
  };

  const fetchData = useCallback(() => {
    (async () => {
      const atEnd = grouped ? state.phase === "done" : !state.hasMore;

      if (
        window.innerHeight + document.documentElement.scrollTop + 2.5 <
          document.documentElement.offsetHeight ||
        state.isLoading ||
        atEnd ||
        !userContext.fetchedUser
      ) {
        return;
      }

      setState((s) => ({
        ...s,
        isLoading: true,
        error: null,
      }));

      try {
        if (!grouped) {
          const data =
            role === "venue"
              ? await getEventsByVenueId(userContext.user.venueId)
              : await getEvents({
                  country: userContext.user.country,
                  id: state.data.slice(-1)[0]?.lastEvaluatedKey?.id,
                  date: state.data.slice(-1)[0]?.lastEvaluatedKey?.date,
                  limit,
                });
          setState((s) => ({
            ...s,
            data: [...s.data, ...data],
            now: Date.now(),
            hasMore: role !== "venue" && data.length === limit,
          }));
        } else {
          const bucketKey = state.phase;
          const { today, tomorrow, yesterday } = getDayWindows();
          const windows = {
            tonight: { fromDate: today, toDate: today },
            upcoming: { fromDate: tomorrow, sort: "asc" },
            past: { toDate: yesterday },
          };
          const cursor = state.cursors[bucketKey];
          const page = await getEvents({
            ...windows[bucketKey],
            dj: role === "dj" ? userContext.user.userId : undefined,
            country: role === "admin" ? userContext.user.country : undefined,
            id: cursor?.id,
            date: cursor?.date,
            limit,
          });
          const nextCursor = page.slice(-1)[0]?.lastEvaluatedKey;
          const nextPhase = nextCursor
            ? bucketKey
            : PHASES[PHASES.indexOf(bucketKey) + 1] || "done";

          setState((s) => ({
            ...s,
            buckets: {
              ...s.buckets,
              [bucketKey]: [...s.buckets[bucketKey], ...page],
            },
            cursors: { ...s.cursors, [bucketKey]: nextCursor },
            phase: nextPhase,
            now: Date.now(),
          }));
        }
      } catch (error) {
        setState((s) => ({
          ...s,
          error,
        }));
      } finally {
        setState((s) => ({
          ...s,
          isLoading: false,
        }));
      }
    })();
  }, [
    grouped,
    role,
    userContext,
    state.data,
    state.hasMore,
    state.isLoading,
    state.phase,
    state.cursors,
  ]);

  useEffect(() => {
    fetchData();

    window.addEventListener("scroll", fetchData);

    return () => window.removeEventListener("scroll", fetchData);
  }, [fetchData]);

  const renderCard = (item) => (
    <Grid
      item
      xs={12}
      md={6}
      lg={4}
      xl={3}
      key={item.id}
      sx={{ background: "transparent" }}
    >
      <EventCard
        item={item}
        role={role}
        now={state.now}
        imageTimestamp={imageTimestamps[item.id]}
        onEdit={handleEdit(item)}
        onDelete={handleDelete(item)}
        onSendPostEventEmail={handleSendPostEventEmail(item)}
      />
    </Grid>
  );

  return (
    <>
      <Grid
        container
        p={4}
        spacing={4}
        sx={{ maxWidth: "1600px", paddingTop: "80px" }}
        justifyContent={
          totalItems === 0 && !state.isLoading ? "center" : "left"
        }
      >
        {["venue", "admin"].includes(role) && (
          <Grid
            item
            xs={12}
            sx={{
              position: "sticky",
              top: "80px",
              zIndex: 10,
              paddingBottom: 1,
            }}
          >
            <Button type="submit" onClick={handleCreate} variant="contained">
              Create Event
            </Button>
          </Grid>
        )}
        {grouped
          ? SECTIONS.filter(({ key }) => state.buckets[key].length > 0).map(
              ({ key, label }) => (
                <Grid item xs={12} key={key}>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "left",
                      color: "white",
                      marginBottom: "10px",
                    }}
                  >
                    {label}
                  </Typography>
                  <Grid container spacing={4}>
                    {state.buckets[key].map(renderCard)}
                  </Grid>
                </Grid>
              )
            )
          : state.data.map(renderCard)}
        {emptyKey.length > 0 && (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <EmptyState emptyStateKey={emptyKey} emptyStateHeight="80vh" />
          </Grid>
        )}
      </Grid>
      {state.isLoading && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      )}
      {state.sendPostEventEmail && (
        <PostEventEmailModal
          event={state.event}
          handleConfirm={state.handleSendPostEventEmailConfirm}
          handleClose={handleSendPostEventEmailClose}
        />
      )}
      {state.open && (
        <EventModal
          venueId={userContext.user?.venueId}
          event={state.event}
          handleConfirm={state.handleConfirm}
          handleClose={handleClose}
        />
      )}
      {state.delete && (
        <DeleteModal
          name={state.deleteName}
          handleConfirm={state.handleDeleteConfirm}
          handleClose={handleDeleteClose}
        />
      )}
    </>
  );
}
